import React, { useState, useRef, useEffect, useCallback } from 'react';
import './RichTextEditor.css';

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = 'Enter content here...',
  height = '400px',
  readOnly = false
}) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showFontSizePicker, setShowFontSizePicker] = useState(false);
  const [showFontFamilyPicker, setShowFontFamilyPicker] = useState(false);
  const [selectedFontFamily, setSelectedFontFamily] = useState('');
  const [selectedFontSize, setSelectedFontSize] = useState('');

  const fontSizes = [
    { label: '8px', value: '8px' },
    { label: '10px', value: '10px' },
    { label: '12px', value: '12px' },
    { label: '14px', value: '14px' },
    { label: '16px', value: '16px' },
    { label: '18px', value: '18px' },
    { label: '20px', value: '20px' },
    { label: '24px', value: '24px' },
    { label: '28px', value: '28px' },
    { label: '32px', value: '32px' },
    { label: '36px', value: '36px' },
    { label: '48px', value: '48px' }
  ];

  const fontFamilies = [
    { label: 'Arial', value: 'Arial, sans-serif' },
    { label: 'Helvetica', value: 'Helvetica, sans-serif' },
    { label: 'Times New Roman', value: 'Times New Roman, serif' },
    { label: 'Georgia', value: 'Georgia, serif' },
    { label: 'Verdana', value: 'Verdana, sans-serif' },
    { label: 'Courier New', value: 'Courier New, monospace' },
    { label: 'Comic Sans MS', value: 'Comic Sans MS, cursive' },
    { label: 'Impact', value: 'Impact, sans-serif' }
  ];

  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF6600', '#FFCC00', '#00FF00', '#0066FF', '#6600FF',
    '#FF00CC', '#FF6666', '#66FF66', '#6666FF', '#FFB366', '#66B3FF'
  ];

  const highlightColors = [
    '#FFFF00', '#FFE066', '#FFB366', '#FF9999', '#99FF99', '#99CCFF',
    '#FF99FF', '#CCCCCC', '#FFE6CC', '#E6F3FF', '#F0F8FF', '#FFF8DC'
  ];

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);



  const execCommand = useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleContentChange();
  }, []);

  const handleContentChange = useCallback(() => {
    if (editorRef.current && onChange) {
      const html = editorRef.current.innerHTML;
      setTimeout(() => onChange(html), 0); // defer to next tick
    }
  }, [onChange]);



  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      execCommand('indent');
    }

    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        case 'z':
          e.preventDefault();
          execCommand('undo');
          break;
        case 'y':
          e.preventDefault();
          execCommand('redo');
          break;
        default:
          break;
      }
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };

  const insertTable = () => {
    const rows = prompt('Number of rows:', '3');
    const cols = prompt('Number of columns:', '3');
    if (rows && cols) {
      let table = '<table border="1" style="border-collapse: collapse; width: 100%;">';
      for (let i = 0; i < parseInt(rows); i++) {
        table += '<tr>';
        for (let j = 0; j < parseInt(cols); j++) {
          table += '<td style="padding: 8px;">Cell</td>';
        }
        table += '</tr>';
      }
      table += '</table>';
      execCommand('insertHTML', table);
    }
  };

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
    setShowFontSizePicker(false);
    setShowFontFamilyPicker(false);
  };

  const toggleFontSizePicker = () => {
    setShowFontSizePicker(!showFontSizePicker);
    setShowColorPicker(false);
    setShowFontFamilyPicker(false);
  };

  const toggleFontFamilyPicker = () => {
    setShowFontFamilyPicker(!showFontFamilyPicker);
    setShowColorPicker(false);
    setShowFontSizePicker(false);
  };

  const applyColor = (color) => {
    execCommand('foreColor', color);
    setShowColorPicker(false);
  };

  const applyHighlight = (color) => {
    execCommand('backColor', color);
    setShowColorPicker(false);
  };

  const applyFontFamily = (fontFamily) => {
    execCommand('fontName', fontFamily);
    setSelectedFontFamily(fontFamily);
    setShowFontFamilyPicker(false);
  };

  const applyFontSize = (size) => {
    execCommand('fontSize', '7');
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const span = document.createElement('span');
      span.style.fontSize = size;
      try {
        range.surroundContents(span);
      } catch (e) {
        range.insertNode(span);
      }
    }
    setSelectedFontSize(size);
    setShowFontSizePicker(false);
    handleContentChange();
  };


  const ToolbarButton = ({ onClick, children, title, active = false, disabled = false, className = '' }) => (
    <button
      type="button"
      className={`toolbar-btn ${active ? 'active' : ''} ${disabled ? 'disabled' : ''} ${className}`}
      onClick={onClick}
      title={title}
      disabled={disabled}
    >
      {children}
    </button>
  );

  const ToolbarDropdown = ({ children, isOpen, onToggle, title }) => (
    <div className="toolbar-dropdown">
      <button
        type="button"
        className="toolbar-btn dropdown-toggle"
        onClick={onToggle}
        title={title}
      >
        {children}
        <span className="dropdown-arrow">▼</span>
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div className={`rich-text-editor ${isFocused ? 'focused' : ''} ${readOnly ? 'readonly' : ''}`}>
      {!readOnly && (
        <div className="toolbar">
          {/* Font Family & Size */}
          <div className="toolbar-group">
            <ToolbarDropdown
              isOpen={showFontFamilyPicker}
              onToggle={toggleFontFamilyPicker}
              title="Font Family"
            >
              <select
                className="font-family-select"
                onChange={(e) => applyFontFamily(e.target.value)}
                value={selectedFontFamily}
              >
                <option value="">Select Font</option>
                {fontFamilies.map((font) => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                    {font.label}
                  </option>
                ))}
              </select>
            </ToolbarDropdown>

            <ToolbarDropdown
              isOpen={showFontSizePicker}
              onToggle={toggleFontSizePicker}
              title="Font Size"
            >
              <select
                className="font-size-select"
                onChange={(e) => applyFontSize(e.target.value)}
                value={selectedFontSize}
              >
                <option value="">Select Size</option>
                {fontSizes.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </select>
            </ToolbarDropdown>
          </div>

          {/* Text Formatting */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={() => execCommand('bold')}
              title="Bold (Ctrl+B)"
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('italic')}
              title="Italic (Ctrl+I)"
            >
              <em>I</em>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('underline')}
              title="Underline (Ctrl+U)"
            >
              <u>U</u>
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('strikeThrough')}
              title="Strikethrough"
            >
              <s>S</s>
            </ToolbarButton>
          </div>

          {/* Superscript/Subscript */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={() => execCommand('superscript')}
              title="Superscript"
            >
              X²
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('subscript')}
              title="Subscript"
            >
              X₂
            </ToolbarButton>
          </div>

          {/* Text Color & Highlight */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={toggleColorPicker}
              title="Text Color"
            >
              A
            </ToolbarButton>
            {showColorPicker && (
              <div className="color-picker">
                <div className="color-section">
                  <h4>Text Color</h4>
                  <div className="color-grid">
                    {colors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className="color-option"
                        onClick={() => applyColor(color)}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
                <div className="color-section">
                  <h4>Highlight Color</h4>
                  <div className="color-grid">
                    {highlightColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className="color-option"
                        onClick={() => applyHighlight(color)}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Alignment */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={() => execCommand('justifyLeft')}
              title="Align Left"
            >
              ⬅
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('justifyCenter')}
              title="Align Center"
            >
              ↔
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('justifyRight')}
              title="Align Right"
            >
              ➡
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('justifyFull')}
              title="Justify"
            >
              ⬌
            </ToolbarButton>
          </div>

          {/* Lists */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={() => execCommand('insertUnorderedList')}
              title="Bullet List"
            >
              •
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('insertOrderedList')}
              title="Numbered List"
            >
              1.
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('outdent')}
              title="Decrease Indent"
            >
              ⬅
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('indent')}
              title="Increase Indent"
            >
              ➡
            </ToolbarButton>
          </div>

          {/* Quotes & Code */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={() => execCommand('formatBlock', 'blockquote')}
              title="Blockquote"
            >
              "
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('formatBlock', 'pre')}
              title="Code Block"
            >
              &lt;/&gt;
            </ToolbarButton>
          </div>

          {/* Links & Media */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={insertLink}
              title="Insert Link"
            >
              🔗
            </ToolbarButton>
            <ToolbarButton
              onClick={insertImage}
              title="Insert Image"
            >
              🖼️
            </ToolbarButton>
            <ToolbarButton
              onClick={insertTable}
              title="Insert Table"
            >
              ⊞
            </ToolbarButton>
          </div>

          {/* Clear Formatting */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={() => execCommand('removeFormat')}
              title="Clear Formatting"
            >
              Tx
            </ToolbarButton>
          </div>

          {/* Undo/Redo */}
          <div className="toolbar-group">
            <ToolbarButton
              onClick={() => execCommand('undo')}
              title="Undo (Ctrl+Z)"
            >
              ↶
            </ToolbarButton>
            <ToolbarButton
              onClick={() => execCommand('redo')}
              title="Redo (Ctrl+Y)"
            >
              ↷
            </ToolbarButton>
          </div>
        </div>
      )}

      <div
        ref={editorRef}
        className="editor-content"
        contentEditable={!readOnly}
        onInput={handleContentChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        style={{ height }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {readOnly && (
        <div className="readonly-overlay">
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;